import {ChangeEvent, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {FormProvider, UseFormSetValue, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {toast} from '@utilities/toast/toast.tsx'
import {errorHandler} from '@utilities/helpers.ts'
import {
    AlertCircleIcon,
    Attachment02Icon,
    File04Icon,
    InfoCircleIcon,
    Mail01Icon,
    RefreshCw05Icon,
    Send01Icon,
    Trash01Icon,
    UserSquareIcon,
    Users01Icon
} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {InputText} from '@/components/commons/input-text/InputText'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {StyledProjectWarningMissingBanner} from '@/features/projects/components/project-warning-missing-banner/style'
import {MAILER_FORM_DEFAULTS, MailerFormSchema, MailerFormValues} from '@/features/mailer/types'
import {
    ALLOWED_ATTACHMENT_ACCEPT,
    MAILER_SEND_LOCKED,
    MAX_TOTAL_ATTACHMENT_SIZE,
    PREVIEW_DEBOUNCE_MS,
    SUBJECT_MAX_LENGTH
} from '@/features/mailer/constants'
import {
    buildRecipientsPayload,
    countConsultantFilters,
    countPatientFilters,
    formatBytes,
    isAllowedAttachment,
    plainTextBodyToHtml
} from '@/features/mailer/utils'
import {useDebouncedValue} from '@/hooks/useDebouncedValue'
import {useRecipientsPreview} from '@/features/mailer/queries/useRecipientsPreview'
import {useSendMail} from '@/features/mailer/queries/useSendMail'
import {useSendTestMail} from '@/features/mailer/queries/useSendTestMail'
import {MailerCardCheckbox} from '@/features/mailer/components/mailer-card-checkbox/MailerCardCheckbox'
import {MailerPatientStatusSegmented} from '@/features/mailer/components/mailer-segmented/MailerPatientStatusSegmented'
import {MailerFilterAccordion} from '@/features/mailer/components/mailer-accordion/MailerFilterAccordion'
import {PatientFiltersPanel} from '@/features/mailer/components/patient-filters-panel/PatientFiltersPanel'
import {ConsultantFiltersPanel} from '@/features/mailer/components/consultant-filters-panel/ConsultantFiltersPanel'
import {MailerRecipientCounter} from '@/features/mailer/components/mailer-recipient-counter/MailerRecipientCounter'
import {
    StyledActionsGroup,
    StyledAttachmentRow,
    StyledAttachmentsList,
    StyledAttachmentsTotal,
    StyledCharCounter,
    StyledComposeWrapper,
    StyledDropzoneButton,
    StyledEmailContentCard,
    StyledEmailContentHeader,
    StyledHeaderBannerWrapper,
    StyledLockedAction,
    StyledRecipientsGrid,
    StyledSectionHeaderRow,
    StyledStatusSection
} from '@/features/mailer/components/mailer-compose-tab/style'

interface MailerComposeTabProps {
    headerActionsContainer: HTMLDivElement | null
}

export const MailerComposeTab = ({headerActionsContainer}: MailerComposeTabProps) => {
    const {t} = useTranslation()
    const form = useForm<MailerFormValues>({
        resolver: zodResolver(MailerFormSchema),
        defaultValues: MAILER_FORM_DEFAULTS,
        mode: 'onChange'
    })
    const {
        watch,
        setValue,
        register,
        handleSubmit,
        reset,
        formState: {errors, isValid}
    } = form

    const values = watch()
    const [attachments, setAttachments] = useState<File[]>([])
    const fileInputRef = useRef<HTMLInputElement>(null)

    const setField: UseFormSetValue<MailerFormValues> = (name, value) =>
        setValue(name, value as never, {shouldValidate: true, shouldDirty: true})

    // --- Live recipient preview -------------------------------------------------
    // Debounce on the serialized payload: a stable primitive avoids resetting
    // the timer on unrelated re-renders and doubles as a memo key.
    const noRecipientSelected = !values.includePatients && !values.includeConsultants
    const recipientsKey = JSON.stringify(noRecipientSelected ? null : buildRecipientsPayload(values))
    const debouncedRecipientsKey = useDebouncedValue(recipientsKey, PREVIEW_DEBOUNCE_MS)
    const debouncedRecipients = useMemo(
        () => (debouncedRecipientsKey === 'null' ? null : JSON.parse(debouncedRecipientsKey)),
        [debouncedRecipientsKey]
    )
    const previewQuery = useRecipientsPreview(debouncedRecipients)

    const counts = previewQuery.data?.counts
    const total = noRecipientSelected ? 0 : counts?.total ?? 0

    // --- Active filters ----------------------------------------------------------
    const patientFiltersCount = countPatientFilters(values)
    const consultantFiltersCount = countConsultantFilters(values)
    const activeFiltersCount =
        (values.includePatients ? patientFiltersCount : 0) + (values.includeConsultants ? consultantFiltersCount : 0)
    const hasNoFilters = !noRecipientSelected && activeFiltersCount === 0

    // --- Attachments ---------------------------------------------------------------
    const totalAttachmentsSize = attachments.reduce((sum, file) => sum + file.size, 0)

    const handleAttach = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        event.target.value = ''

        const validFiles: File[] = []
        for (const file of files) {
            if (!isAllowedAttachment(file)) {
                toast.error(t('mailer:attachments_invalid_type', {name: file.name}))
                continue
            }
            validFiles.push(file)
        }

        const nextSize = totalAttachmentsSize + validFiles.reduce((sum, file) => sum + file.size, 0)
        if (nextSize > MAX_TOTAL_ATTACHMENT_SIZE) {
            toast.error(t('mailer:attachments_too_large', {limit: formatBytes(MAX_TOTAL_ATTACHMENT_SIZE)}))
            return
        }

        if (validFiles.length > 0) setAttachments(previous => [...previous, ...validFiles])
    }

    const removeAttachment = (index: number) =>
        setAttachments(previous => previous.filter((_, itemIndex) => itemIndex !== index))

    // --- Actions ---------------------------------------------------------------------
    const handleFullReset = () => {
        reset(MAILER_FORM_DEFAULTS)
        setAttachments([])
    }

    const sendMailMutation = useSendMail({
        onSuccess: data => {
            toast.success(t('mailer:toast_sent', {count: data.recipientCount}))
            handleFullReset()
        },
        onError: errorHandler
    })

    const testMailMutation = useSendTestMail({
        onSuccess: data => toast.success(t('mailer:toast_test_sent', {email: data.sentTo})),
        onError: errorHandler
    })

    const isSending = sendMailMutation.isPending
    const isTesting = testMailMutation.isPending

    // Enable rules per functional spec §9. The definitive send stays locked
    // during the rollout phase (MAILER_SEND_LOCKED): only test sends go out.
    const hasMissingEmailContent = values.subject.trim().length === 0 || values.body.trim().length === 0
    const isTestSetupIncomplete = noRecipientSelected || hasMissingEmailContent
    const canTest = !isTestSetupIncomplete && !isTesting && !isSending
    const canSend = !MAILER_SEND_LOCKED && isValid && !noRecipientSelected && total > 0 && !isSending

    const onSubmit = handleSubmit(formValues => {
        if (MAILER_SEND_LOCKED) return
        sendMailMutation.mutate({
            subject: formValues.subject,
            body: plainTextBodyToHtml(formValues.body),
            recipients: buildRecipientsPayload(formValues),
            attachments
        })
    })

    const handleTestSend = () =>
        testMailMutation.mutate({
            subject: values.subject,
            body: plainTextBodyToHtml(values.body),
            attachments
        })

    return (
        <FormProvider {...form}>
            <StyledComposeWrapper onSubmit={onSubmit} noValidate>
                <section>
                    <StyledSectionHeaderRow>
                        <label>{t('mailer:recipients_label')}</label>
                        <Button type="button" variant="ghost" size="sm" onClick={handleFullReset}>
                            <RefreshCw05Icon />
                            {t('mailer:reset_cta')}
                        </Button>
                    </StyledSectionHeaderRow>
                    <StyledRecipientsGrid>
                        <MailerCardCheckbox
                            checked={values.includePatients}
                            onToggle={() => setField('includePatients', !values.includePatients)}
                            icon={<Users01Icon />}
                            title={t('mailer:recipients_patients')}
                            subtitle={t('mailer:recipients_patients_sub')}
                        />
                        <MailerCardCheckbox
                            checked={values.includeConsultants}
                            onToggle={() => setField('includeConsultants', !values.includeConsultants)}
                            icon={<UserSquareIcon />}
                            title={t('mailer:recipients_staff')}
                            subtitle={t('mailer:recipients_staff_sub')}
                        />
                    </StyledRecipientsGrid>
                </section>

                <div>
                    <MailerRecipientCounter
                        total={total}
                        patientsCount={values.includePatients ? counts?.patients ?? 0 : null}
                        consultantsCount={values.includeConsultants ? counts?.consultants ?? 0 : null}
                        isLoading={!noRecipientSelected && previewQuery.isFetching}
                        recipients={debouncedRecipients}
                        includePatients={values.includePatients}
                        includeConsultants={values.includeConsultants}
                    />
                </div>

                {values.includePatients && (
                    <StyledStatusSection>
                        <div className="heading">
                            <label>{t('mailer:patient_status_label')}</label>
                            <small>
                                <InfoCircleIcon />
                                {t('mailer:patient_status_hint')}
                            </small>
                        </div>
                        <MailerPatientStatusSegmented
                            value={values.patientStatus}
                            onChange={value => setField('patientStatus', value)}
                        />
                    </StyledStatusSection>
                )}

                {values.includePatients && (
                    <MailerFilterAccordion
                        icon={<Users01Icon />}
                        title={t('mailer:patient_filters_title')}
                        activeCount={patientFiltersCount}
                    >
                        <PatientFiltersPanel />
                    </MailerFilterAccordion>
                )}

                {values.includeConsultants && (
                    <MailerFilterAccordion
                        icon={<UserSquareIcon />}
                        title={t('mailer:staff_filters_title')}
                        activeCount={consultantFiltersCount}
                    >
                        <ConsultantFiltersPanel />
                    </MailerFilterAccordion>
                )}

                <StyledEmailContentCard>
                    <StyledEmailContentHeader>
                        <Mail01Icon />
                        <h3>{t('mailer:email_content_title')}</h3>
                    </StyledEmailContentHeader>
                    <div>
                        <InputText
                            type="text"
                            label={t('mailer:subject_label')}
                            placeholder={t('mailer:subject_placeholder')}
                            maxLength={SUBJECT_MAX_LENGTH}
                            errorMessage={errors.subject?.message}
                            touched
                            {...register('subject')}
                        />
                        <StyledCharCounter>
                            {values.subject.length}/{SUBJECT_MAX_LENGTH}
                        </StyledCharCounter>
                    </div>
                    <TextArea
                        label={t('mailer:body_label')}
                        placeholder={t('mailer:body_placeholder')}
                        rows={6}
                        errorMessage={errors.body?.message}
                        {...register('body')}
                    />
                    <div>
                        <StyledSectionHeaderRow>
                            <label>{t('mailer:attachments_label')}</label>
                        </StyledSectionHeaderRow>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            style={{display: 'none'}}
                            accept={ALLOWED_ATTACHMENT_ACCEPT}
                            onChange={handleAttach}
                        />
                        <StyledDropzoneButton type="button" onClick={() => fileInputRef.current?.click()}>
                            <Attachment02Icon />
                            {t('mailer:attachments_cta')}
                        </StyledDropzoneButton>
                        {attachments.length > 0 && (
                            <>
                                <StyledAttachmentsList>
                                    {attachments.map((file, index) => (
                                        <StyledAttachmentRow key={`${file.name}-${index}`}>
                                            <div>
                                                <File04Icon />
                                                <span className="name">{file.name}</span>
                                                <span className="size">{formatBytes(file.size)}</span>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="xs"
                                                shape="square"
                                                aria-label={t('mailer:attachment_remove')}
                                                onClick={() => removeAttachment(index)}
                                            >
                                                <Trash01Icon />
                                            </Button>
                                        </StyledAttachmentRow>
                                    ))}
                                </StyledAttachmentsList>
                                <StyledAttachmentsTotal>
                                    {t('mailer:attachments_total', {
                                        count: attachments.length,
                                        size: formatBytes(totalAttachmentsSize),
                                        limit: formatBytes(MAX_TOTAL_ATTACHMENT_SIZE)
                                    })}
                                </StyledAttachmentsTotal>
                            </>
                        )}
                    </div>
                </StyledEmailContentCard>
            </StyledComposeWrapper>

            {headerActionsContainer &&
                createPortal(
                    <>
                        {noRecipientSelected && (
                            <StyledHeaderBannerWrapper>
                                <StyledProjectWarningMissingBanner align="center" $variant="error">
                                    <Flexbox gap={1} align="center">
                                        <AlertCircleIcon />
                                        <Text palette="neutral" shade="950" size="textSm">
                                            {t('mailer:error_no_recipient_type')}
                                        </Text>
                                    </Flexbox>
                                </StyledProjectWarningMissingBanner>
                            </StyledHeaderBannerWrapper>
                        )}

                        {hasNoFilters && (
                            <StyledHeaderBannerWrapper>
                                <StyledProjectWarningMissingBanner align="center" $variant="warning">
                                    <Flexbox gap={1} align="center">
                                        <AlertCircleIcon />
                                        <Text palette="neutral" shade="950" size="textSm">
                                            <Trans
                                                i18nKey="mailer:warning_no_filters"
                                                components={[<strong key="bold" />]}
                                            />
                                        </Text>
                                    </Flexbox>
                                </StyledProjectWarningMissingBanner>
                            </StyledHeaderBannerWrapper>
                        )}

                        <StyledActionsGroup>
                            {isTestSetupIncomplete ? (
                                <Tooltip content={t('mailer:test_send_locked_tooltip')} triggerProps={{asChild: true}}>
                                    <StyledLockedAction>
                                        <Button type="button" variant="secondary" disabled onClick={handleTestSend}>
                                            <Mail01Icon />
                                            {t('mailer:test_send_cta')}
                                        </Button>
                                    </StyledLockedAction>
                                </Tooltip>
                            ) : (
                                <Button type="button" variant="secondary" disabled={!canTest} onClick={handleTestSend}>
                                    {isTesting ? <Spinner size={14} /> : <Mail01Icon />}
                                    {t('mailer:test_send_cta')}
                                </Button>
                            )}
                            <Tooltip content={t('mailer:send_locked_tooltip')} triggerProps={{asChild: true}}>
                                <StyledLockedAction>
                                    <Button type="button" variant="primary" disabled={!canSend} onClick={onSubmit}>
                                        {isSending ? (
                                            <>
                                                <Spinner size={14} />
                                                {t('mailer:sending')}
                                            </>
                                        ) : (
                                            <>
                                                <Send01Icon />
                                                {t('mailer:send_cta', {count: total})}
                                            </>
                                        )}
                                    </Button>
                                </StyledLockedAction>
                            </Tooltip>
                        </StyledActionsGroup>
                    </>,
                    headerActionsContainer
                )}
        </FormProvider>
    )
}

MailerComposeTab.displayName = 'MailerComposeTab'
